import type { CarBounds, Vector3Tuple } from '../types';

/**
 * 에셋에서 네 바퀴를 찾아내는 순수 로직.
 *
 * 왜 매니페스트에 선택자를 적지 않는가:
 *   기존 역할 매핑(`selectors`)은 메시·머티리얼 **이름**으로 고릅니다. 바퀴에는 그
 *   방식이 통하지 않습니다. `rim` 역할은 네 바퀴를 한 덩어리로 모으는데, 굴리려면
 *   모서리별로 갈라야 하고 이름만으로는 앞뒤·좌우를 알 수 없습니다(Sorento는 모든
 *   머티리얼이 `template_fabrics__carpet_N`이고 M8은 `Wheel_04.001`처럼 번호만 다릅니다).
 *   대신 **위치**로 찾습니다. 매니페스트에 이미 축 좌표 실측값이 있어서, 일곱 모델에
 *   선택자를 새로 적지 않고도 같은 규칙으로 처리됩니다.
 *
 * 왜 허브를 실측값 그대로 쓰지 않는가:
 *   `bounds`의 축 좌표는 카메라·치수 검증용으로 뽑은 값이라 실제 휠 중심과 몇 cm
 *   어긋납니다. 718 Boxster는 뒤축이 7cm 벗어나 있어서, 실측값을 중심으로 상자를
 *   잡으면 타이어가 상자 밖으로 밀려나 rim만 잡혔습니다. 그러면 rim은 돌고 타이어는
 *   멈춰 있는 상태가 됩니다. 그래서 실측값은 **찾을 자리를 좁히는 데만** 쓰고,
 *   중심은 그 자리에서 발견한 타이어의 AABB 중심으로 다시 잡습니다.
 */

/** 클러스터링에 필요한 메시 정보만. three에 의존하지 않게 좌표만 받습니다. */
export interface MeshBox {
  /** 씬에서 이 메시를 다시 찾을 식별자. three 어댑터가 채웁니다. */
  id: string;
  /** 앱 좌표계 AABB. */
  min: Vector3Tuple;
  max: Vector3Tuple;
  /** 삼각형 수. 한 바퀴의 주 형상(타이어)을 고르는 기준입니다. */
  triangles: number;
}

export type WheelCorner = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

export interface WheelCluster {
  corner: WheelCorner;
  /** 회전 중심. 발견한 타이어의 AABB 중심입니다. */
  center: Vector3Tuple;
  /** 이 바퀴와 함께 돌아야 하는 메시들. */
  meshIds: string[];
  triangles: number;
}

/** 앞바퀴는 조향도 받습니다. */
export const FRONT_CORNERS: WheelCorner[] = ['frontLeft', 'frontRight'];

/* --- 탐색 규칙 --- */

/** 실측 축 좌표에서 이만큼 떨어진 곳까지 타이어 후보로 봅니다. */
const ANCHOR_SEARCH_RADIUS_PER_TIRE = 0.6;
/** 타이어로 인정할 지름 범위(타이어 반지름 대비). 2.0이 정확히 실측 지름입니다. */
const ANCHOR_DIAMETER_MIN = 1.2;
const ANCHOR_DIAMETER_MAX = 2.4;
/** 타이어는 좌우로 얇습니다. 이보다 두꺼우면 좌우 바퀴가 한 덩어리인 경우입니다. */
const ANCHOR_WIDTH_MAX_PER_TIRE = 1.6;

/** 중심을 다시 잡은 뒤 함께 돌릴 것을 모으는 상자. */
const MEMBER_RADIAL_PER_TIRE = 1.06;
const MEMBER_HALF_WIDTH_PER_TIRE = 0.62;

/**
 * 네 바퀴의 삼각형 수가 이 비율 안에 들어야 신뢰합니다.
 *
 * 한쪽만 절반이면 그 바퀴의 형상을 놓친 것이고, 그대로 돌리면 한 바퀴만 굴러가는
 * 눈에 띄는 고장이 됩니다. 좌우 휠은 보통 같은 메시를 미러링하므로 정상이면 거의
 * 같습니다. 캘리퍼 유무 정도의 차이는 허용합니다.
 */
const BALANCE_TOLERANCE = 0.55;

const size = (box: MeshBox, axis: number): number => box.max[axis] - box.min[axis];
const center = (box: MeshBox): Vector3Tuple => [
  (box.min[0] + box.max[0]) / 2,
  (box.min[1] + box.max[1]) / 2,
  (box.min[2] + box.max[2]) / 2,
];

function distance(a: Vector3Tuple, b: Vector3Tuple): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function expectedHubs(bounds: CarBounds): Array<{ corner: WheelCorner; at: Vector3Tuple }> {
  const { frontWheelX, rearWheelX, wheelHalfTrack, tireOuterRadius } = bounds;
  return [
    { corner: 'frontLeft', at: [frontWheelX, tireOuterRadius, -wheelHalfTrack] },
    { corner: 'frontRight', at: [frontWheelX, tireOuterRadius, wheelHalfTrack] },
    { corner: 'rearLeft', at: [rearWheelX, tireOuterRadius, -wheelHalfTrack] },
    { corner: 'rearRight', at: [rearWheelX, tireOuterRadius, wheelHalfTrack] },
  ];
}

/**
 * 타이어로 볼 만한 메시인지. 바퀴 하나에 해당하는 크기·모양만 통과합니다.
 *
 * 지름 상한이 중요합니다. 지오메트리가 병합된 에셋은 네 바퀴가 한 프리미티브에 들어
 * 있어서 차폭만큼 넓게 나옵니다. 그걸 타이어로 잡으면 차체 전체가 회전합니다.
 */
function looksLikeATire(box: MeshBox, tireOuterRadius: number): boolean {
  const diameter = Math.max(size(box, 0), size(box, 1)) / tireOuterRadius;
  const width = size(box, 2) / tireOuterRadius;
  return diameter >= ANCHOR_DIAMETER_MIN && diameter <= ANCHOR_DIAMETER_MAX && width <= ANCHOR_WIDTH_MAX_PER_TIRE;
}

function isInsideWheelVolume(box: MeshBox, hub: Vector3Tuple, tireOuterRadius: number): boolean {
  const radial = tireOuterRadius * MEMBER_RADIAL_PER_TIRE;
  const half = tireOuterRadius * MEMBER_HALF_WIDTH_PER_TIRE;
  return (
    box.min[0] >= hub[0] - radial &&
    box.max[0] <= hub[0] + radial &&
    box.min[1] >= hub[1] - radial &&
    box.max[1] <= hub[1] + radial &&
    box.min[2] >= hub[2] - half &&
    box.max[2] <= hub[2] + half
  );
}

/**
 * 네 바퀴를 찾습니다.
 *
 * @returns 네 개를 모두 찾고 균형이 맞을 때만 배열. 하나라도 확신할 수 없으면 null이고,
 *   그 경우 호출한 쪽은 바퀴를 굴리지 않습니다. 억지로 돌리면 차체가 딸려 돌거나
 *   한 바퀴만 구르는데, 둘 다 굴리지 않는 것보다 나쁩니다.
 */
export function findWheelClusters(boxes: MeshBox[], bounds: CarBounds): WheelCluster[] | null {
  const { tireOuterRadius } = bounds;
  const searchRadius = tireOuterRadius * ANCHOR_SEARCH_RADIUS_PER_TIRE;
  const clusters: WheelCluster[] = [];
  // 한 메시가 두 바퀴에 들어가면 회전이 두 번 먹습니다.
  const claimed = new Set<string>();

  // eslint-disable-next-line no-restricted-syntax
  for (const hub of expectedHubs(bounds)) {
    const anchor = boxes
      .filter(
        (box) =>
          !claimed.has(box.id) &&
          distance(center(box), hub.at) <= searchRadius &&
          looksLikeATire(box, tireOuterRadius)
      )
      // 타이어는 바퀴에서 가장 삼각형이 많은 부품입니다.
      .sort((a, b) => b.triangles - a.triangles)[0];

    if (anchor === undefined) {
      return null;
    }

    const corrected = center(anchor);
    const members = boxes.filter(
      (box) => !claimed.has(box.id) && isInsideWheelVolume(box, corrected, tireOuterRadius)
    );
    members.forEach((box) => claimed.add(box.id));

    clusters.push({
      corner: hub.corner,
      center: corrected,
      meshIds: members.map((box) => box.id),
      triangles: members.reduce((sum, box) => sum + box.triangles, 0),
    });
  }

  const triangleCounts = clusters.map((cluster) => cluster.triangles);
  const smallest = Math.min(...triangleCounts);
  const largest = Math.max(...triangleCounts);
  if (smallest === 0 || smallest / largest < BALANCE_TOLERANCE) {
    return null;
  }

  return clusters;
}
